import { z } from 'zod';

/**
 * Roles within one org, lowest to highest. A member's effective role is the
 * highest role they hold across the org and its groups.
 */
export const ROLES = ['learner', 'judge', 'facilitator', 'author', 'admin', 'owner'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

const RANK: Record<Role, number> = Object.fromEntries(ROLES.map((r, i) => [r, i])) as Record<
  Role,
  number
>;

export function roleRank(role: Role): number {
  return RANK[role];
}

/** True when `held` is at least `required`. */
export function hasRole(held: Role | null | undefined, required: Role): boolean {
  return held != null && RANK[held] >= RANK[required];
}

/** Highest of the given roles, or null when there are none. */
export function maxRole(roles: Iterable<Role>): Role | null {
  let best: Role | null = null;
  for (const r of roles) {
    if (best === null || RANK[r] > RANK[best]) best = r;
  }
  return best;
}

/**
 * Access a user has to an org.
 * - `role`: their own membership role on this org, if any.
 * - `viaAncestor`: they are an admin (or owner) of an ancestor org, which
 *   grants read access to this org's reports but not management.
 */
export interface OrgAccess {
  role: Role | null;
  viaAncestor: boolean;
}

export function canView(access: OrgAccess): boolean {
  return access.role !== null || access.viaAncestor;
}

export function canManage(access: OrgAccess): boolean {
  return hasRole(access.role, 'admin');
}
